// Recognition shared constants and small helpers
// Keep minimal and framework-agnostic so they can be reused across pages/components.

export const RECOGNITION_REDEMPTION_METHOD_CHOICES = [
  { text: 'Gift card', value: 'VOUCHER' },
  { text: 'Cash added to salary payment', value: 'BANK_ACCOUNT' },
  { text: 'Cash paid into Savings pot', value: 'WPS' },
];

export const RECOGNITION_BUDGET_CADENCE_OPTIONS = [
  { text: 'Year', value: 'YEAR' },
  { text: 'Quarter', value: 'QUARTER' },
  { text: 'Month', value: 'MONTH' },
];

// Days per month helper for recognition budget/year-start logic.
// February is treated as 29 days since the exact year is irrelevant for financial year start.
export const DAYS_IN_MONTH = {
  1: 31,
  2: 29,
  3: 31,
  4: 30,
  5: 31,
  6: 30,
  7: 31,
  8: 31,
  9: 30,
  10: 31,
  11: 30,
  12: 31,
};

export const MONTH_OPTIONS = [
  { text: 'January', value: 1 },
  { text: 'February', value: 2 },
  { text: 'March', value: 3 },
  { text: 'April', value: 4 },
  { text: 'May', value: 5 },
  { text: 'June', value: 6 },
  { text: 'July', value: 7 },
  { text: 'August', value: 8 },
  { text: 'September', value: 9 },
  { text: 'October', value: 10 },
  { text: 'November', value: 11 },
  { text: 'December', value: 12 },
];

export const RECOGNITION_AWARD_TYPES = {
  OPEN: 'OPEN',
  PROGRAM: 'PROGRAM',
  MILESTONE: 'MILESTONE',
};

export const RECOGNITION_APPROVAL_TYPES = {
  NONE: 'NONE',
  AMOUNT: 'AMOUNT',
  ALL: 'ALL',
};

// Milestone events
export const RECOGNITION_MILESTONE_EVENT_TYPES = {
  BIRTHDAY: 'BIRTHDAY',
  ANNIVERSARY: 'ANNIVERSARY',
};

export const RECOGNITION_MILESTONE_EVENT_OPTIONS = [
  { text: 'Birthdays', value: RECOGNITION_MILESTONE_EVENT_TYPES.BIRTHDAY },
  { text: 'Service anniversaries', value: RECOGNITION_MILESTONE_EVENT_TYPES.ANNIVERSARY },
];

export const toMonthNumber = v => {
  if (v === null || v === undefined || v === '') {
    return undefined;
  }
  if (Number.isFinite(v)) {
    return Number(v);
  }
  const s = String(v).trim().toLowerCase();
  const found = MONTH_OPTIONS.find(m => m.text.toLowerCase() === s);
  return found ? found.value : undefined;
};

// Analytics

export const RECOGNITION_ANALYTICS_DATE_RANGES = [
  { text: 'Last 12 months', value: 'last_12_months' },
  { text: 'Last 6 months', value: 'last_6_months' },
  { text: 'Last 3 months', value: 'last_3_months' },
  { text: 'Year to date', value: 'year_to_date' },
];

// Format a Date as a local-time ISO date string (YYYY-MM-DD).
export const toIsoDateString = d =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * Convert a date range selector value to an ISO date string.
 * Shared by TransactionsTable and AnalyticsTab.
 */
export const getDateFromForRange = rangeValue => {
  if (!rangeValue) {
    return null;
  }
  const now = new Date();

  switch (rangeValue) {
    case 'last_30_days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 30);
      return toIsoDateString(d);
    }
    case 'last_90_days': {
      const d = new Date(now);
      d.setDate(d.getDate() - 90);
      return toIsoDateString(d);
    }
    case 'last_year':
    case 'last_12_months': {
      const d = new Date(now);
      d.setFullYear(d.getFullYear() - 1);
      return toIsoDateString(d);
    }
    case 'last_6_months': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 6);
      return toIsoDateString(d);
    }
    case 'last_3_months': {
      const d = new Date(now);
      d.setMonth(d.getMonth() - 3);
      return toIsoDateString(d);
    }
    case 'year_to_date': {
      return toIsoDateString(new Date(now.getFullYear(), 0, 1));
    }
    default:
      return null;
  }
};

// Forward-looking ranges for the recognition "Upcoming" milestone awards tab.
// 'custom' is paired with a date picker; the others resolve via getDateToForRange.
export const UPCOMING_AWARDS_DEFAULT_RANGE = 'next_30_days';

export const UPCOMING_AWARDS_DATE_RANGES = [
  { text: 'Next 30 days', value: 'next_30_days' },
  { text: 'Next 60 days', value: 'next_60_days' },
  { text: 'Next 90 days', value: 'next_90_days' },
  { text: 'Custom date', value: 'custom' },
];

// Number of days each non-custom upcoming range looks ahead.
export const UPCOMING_AWARDS_RANGE_DAYS = {
  next_30_days: 30,
  next_60_days: 60,
  next_90_days: 90,
};

/**
 * Convert an upcoming-awards range selector value to a forward ISO date string
 * (today + N days). Returns null for unknown/custom ranges, where the caller
 * supplies its own date.
 */
export const getDateToForRange = rangeValue => {
  const days = UPCOMING_AWARDS_RANGE_DAYS[rangeValue];
  if (!days) {
    return null;
  }
  const d = new Date();
  d.setDate(d.getDate() + days);
  return toIsoDateString(d);
};

export const toTwoDecimalsOrEmpty = v => {
  const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
  if (!Number.isFinite(n)) {
    return '';
  }
  return (Math.round(n * 100) / 100).toFixed(2);
};
